import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "329",
  "slug": "latitude-and-longitude-explained",
  "title": "Latitude and Longitude Explained: How to Pinpoint Any Place on Earth",
  "excerpt": "Two numbers can locate any point on the planet to within a few metres. Here's how latitude and longitude actually work, how to read coordinates, and why the order matters.",
  "date": "2026-06-02",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1723306743371-38f6666be1a3?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxnbG9iZSUyMGxhdGl0dWRlJTIwZ3JpZHxlbnwxfDB8fHwxNzgwMzkzMjc1fDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "5 min",
  "author": "EarthGuessr Team",
  "content": [
    {
      "type": "paragraph",
      "text": "Every spot on Earth has an address made of just two numbers. Give someone your latitude and longitude and they can find you on the planet to within a few metres, anywhere from the middle of the Pacific to a street corner in Oslo. It's one of humanity's most useful inventions — a grid laid over a sphere. Here's how it works."
    },
    {
      "type": "heading",
      "text": "Latitude: How Far North or South"
    },
    {
      "type": "paragraph",
      "text": "Latitude tells you how far you are from the equator, measured in degrees from 0° to 90°. The equator is 0°. The North Pole is 90° north; the South Pole is 90° south. Lines of latitude run east–west around the globe like rungs on a ladder and are called parallels, because they never meet — they stay the same distance apart all the way to the poles."
    },
    {
      "type": "paragraph",
      "text": "A few parallels have names you've heard: the Tropic of Cancer (about 23.5° N) and the Tropic of Capricorn (about 23.5° S) mark the limits of where the Sun can be directly overhead, and the Arctic and Antarctic Circles (about 66.5° N and S) mark where you can get a full day of darkness or daylight at the solstice. Because parallels stay evenly spaced, one degree of latitude is always about 111 km — a handy constant."
    },
    {
      "type": "heading",
      "text": "Longitude: How Far East or West"
    },
    {
      "type": "paragraph",
      "text": "Longitude tells you how far east or west you are, measured from 0° to 180°. Lines of longitude run north–south from pole to pole and are called meridians. The starting line, 0° longitude, is the Prime Meridian, which by international agreement runs through Greenwich in London. Head east and the numbers climb to 180°; head west and they climb to 180° too, meeting on the far side of the globe near the International Date Line."
    },
    {
      "type": "paragraph",
      "text": "Unlike parallels, meridians are not parallel — they're widest apart at the equator and converge to a single point at each pole. That's why a degree of longitude covers about 111 km at the equator but shrinks toward nothing as you approach the poles."
    },
    {
      "type": "heading",
      "text": "A Short History of the Grid"
    },
    {
      "type": "paragraph",
      "text": "Latitude was always the easy half — you can work it out from the height of the Sun at noon or the angle of the North Star. Longitude was the great unsolved problem of navigation for centuries, because measuring it requires knowing the exact time at a reference point while you're at sea. Sailors with no way to keep accurate time got lost, ran aground, and died for it. The breakthrough came in the 18th century when the English clockmaker John Harrison built a marine chronometer accurate enough to keep reference time on a rolling ship. As for the starting line itself: Greenwich was chosen as the world's Prime Meridian at the 1884 International Meridian Conference in Washington, beating out rivals like Paris."
    },
    {
      "type": "heading",
      "text": "Reading a Coordinate"
    },
    {
      "type": "paragraph",
      "text": "Coordinates come in two common formats. The traditional one uses degrees, minutes, and seconds (DMS): the Eiffel Tower sits at roughly 48° 51′ 30″ N, 2° 17′ 40″ E. The modern, computer-friendly one uses decimal degrees: the same spot is about 48.8584° N, 2.2945° E. To convert, remember there are 60 minutes in a degree and 60 seconds in a minute."
    },
    {
      "type": "paragraph",
      "text": "You'll also see signs instead of letters: positive latitude is north, negative is south; positive longitude is east, negative is west. So the Sydney Opera House at roughly 33.857° S, 151.215° E can be written as -33.857, 151.215. This sign convention is what almost every map app and GPS device uses under the hood."
    },
    {
      "type": "heading",
      "text": "Why the Order Matters"
    },
    {
      "type": "paragraph",
      "text": "The universal convention is latitude first, then longitude — 'lat, long', north-south before east-west. Get the order wrong and you can end up on the wrong side of the planet. A famous demonstration: the coordinate 0°, 0° — zero latitude, zero longitude — lands in the Atlantic Ocean in the Gulf of Guinea, a featureless patch of sea sometimes jokingly called 'Null Island'. It's where a surprising amount of mislabelled location data accidentally washes up, simply because a missing coordinate often defaults to zero."
    },
    {
      "type": "heading",
      "text": "From Coordinates to Real Places"
    },
    {
      "type": "paragraph",
      "text": "Coordinates feel abstract until you use them to actually find somewhere. That's the quiet skill behind every location game: when you're dropped onto an unknown satellite view and asked where on Earth you are, you're really estimating a latitude and longitude from clues. Snow and short shadows suggest a high latitude; lush rainforest suggests you're near the equator; the way the Sun strikes the land hints at which hemisphere you're in. The grid stops being a maths lesson and becomes a tool for reading the planet."
    },
    {
      "type": "paragraph",
      "text": "The best way to make latitude and longitude click is to use them. Open EarthGuessr, get dropped somewhere on the globe, and place your guess — you'll be reasoning in coordinates without even noticing, and the globe will show you exactly how close you got."
    }
  ]
};
